import React, { useCallback, useEffect, useMemo, useState } from "react";
import { countBusinessDaysSince } from "../../utils/businessDays";
import "./Everify.scss";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../Common/Header";
import Button from "../../SharableInputs/Button";
import CustomSingleSelect from "../../SharableInputs/CustomSingleSelect";
import Input from "../../SharableInputs/Input";
import { api } from "../../lib/api";
import CaseFieldsStep from "./EverifySteps/CaseFieldsStep";
import { useCase } from "../../context/CaseContext";
import { clampIsoDateYear } from "../../lib/dateUtils";

const formatLabel = value => value.replace(/_/g, " ").replace(/\s+/g, " ").trim();
const buildOptions = (values = []) =>
  values.map((value, index) => ({
    id: index + 1,
    key: formatLabel(value),
    value
  }));

const citizenshipOptions = buildOptions([
  "US_CITIZEN",
  "NONCITIZEN",
  "LAWFUL_PERMANENT_RESIDENT",
  "ALIEN_AUTHORIZED_TO_WORK",
  "NONCITIZEN_AUTHORIZED_TO_WORK"
]);

const documentAOptions = buildOptions([
  "FOREIGN_PASSPORT_WITH_FORM_I94",
  "FOREIGN_PASSPORT_WITH_I551_STAMP",
  "FORM_I551",
  "FORM_I766",
  "FORM_I94_RECEIPT",
  "US_PASSPORT"
]);

const documentBOptions = buildOptions([
  "CANADIAN_DRIVERS_LICENSE",
  "DAY_CARE_RECORD",
  "DRIVERS_LICENSE",
  "GOVERNMENT_ID_CARD",
  "HOSPITAL_RECORD",
  "MILITARY_DEPENDENT_ID_CARD",
  "NATIVE_AMERICAN_TRIBAL_DOCUMENT",
  "SCHOOL_ID_CARD",
  "SCHOOL_RECORD",
  "US_COAST_GUARD_CARD",
  "US_MILITARY_CARD",
  "VOTER_REGISTRATION_CARD"
]);

const documentCOptions = buildOptions([
  "DS_1350",
  "EMPLOYMENT_AUTHORIZATION_DOCUMENT",
  "FORM_I179",
  "FORM_I197",
  "FS_240",
  "FS_545",
  "NATIVE_AMERICAN_TRIBAL_DOCUMENT",
  "SOCIAL_SECURITY_CARD",
  "US_BIRTH_CERTIFICATE"
]);
const documentSubTypeOptions = buildOptions(["DRIVERS_LICENSE", "STATE_ID_CARD"]);
const usStateOptions = buildOptions([
  "AK", "AL", "AR", "AS", "AZ", "CA", "CO", "CT", "DC", "DE", "FL", "GA", "GU", "HI", "IA", "ID",
  "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MP", "MS", "MT", "NC", "ND",
  "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "PR", "RI", "SC", "SD", "TN", "TX",
  "UT", "VA", "VI", "VT", "WA", "WI", "WV", "WY"
]);
const reasonForDelayOptions = buildOptions([
  "AWAITING_SOCIAL_SECURITY_NUMBER",
  "TECHNICAL_PROBLEMS",
  "AUDIT_REVEALED_THAT_NEW_HIRE_WAS_NOT_RUN",
  "FEDERAL_CONTRACTOR",
  "OTHER",
  "AWAITING_RESPONSE_ON_CASE_WITH_INCORRECT_INFO"
]);
const countryCodes = buildOptions([
  "AFG","AGO","ALB","AND","ARE","ARG","ARM","ATG","AUS","AUT","AZE","BDI","BEL","BEN","BFA","BGD","BGR","BHR",
  "BHS","BIH","BLR","BLZ","BOL","BRA","BRB","BRN","BTN","BWA","CAF","CAN","CHE","CHL","CHN","CIV","CMR","COD",
  "COG","COL","COM","CPV","CRI","CUB","CYP","CZE","DEU","DJI","DMA","DNK","DOM","DZA","ECU","EGY","ERI","ESP",
  "EST","ETH","FIN","FJI","FRA","FSM","GAB","GBR","GEO","GHA","GIN","GMB","GNB","GNQ","GRC","GRD","GTM","GUY",
  "HND","HRV","HTI","HUN","IDN","IND","IRL","IRN","IRQ","ISL","ISR","ITA","JAM","JOR","JPN","KAZ","KEN","KGZ",
  "KHM","KIR","KNA","KOR","KVO","KWT","LAO","LBN","LBR","LBY","LCA","LIE","LKA","LSO","LTU","LUX","LVA","MAR",
  "MCO","MDA","MDG","MDV","MEX","MHL","MKD","MLI","MLT","MMR","MNE","MNG","MOZ","MRT","MUS","MWI","MYS","NAM",
  "NER","NGA","NIC","NLD","NOR","NPL","NRU","NZL","OMN","PAK","PAN","PER","PHL","PLW","PNG","POL","PRK","PRT",
  "PRY","PSE","QAT","ROU","RUS","RWA","SAU","SDN","SEN","SGP","SLB","SLE","SLV","SMR","SOM","SRB","SSD","STP",
  "SUR","SVK","SVN","SWE","SWZ","SYC","SYR","TCD","TGO","THA","TJK","TKM","TLS","TON","TTO","TUN","TUR","TUV",
  "TWN","TZA","UGA","UKR","URY","UZB","VAT","VCT","VEN","VNM","VUT","WSM","YEM","ZAF","ZMB","ZWE"
]);
const CASE_FIELDS_ENDPOINT = "/everify/casefields";
const CREATE_CASE_ENDPOINT = "/everify/createcase";
const ALWAYS_VISIBLE_OPTIONAL_FIELDS = new Set([
  "middle_initial",
  "phone_number",
  "employee_email_address",
  "employee_address1",
  "employee_address2",
  "employee_city",
  "employee_zip_code",
  "reason_for_delay_code",
  "reason_for_delay_description"
]);

const STEP_ONE_CASE_FIELD_NAMES = new Set([
  "citizenship_status_code",
  "ssn",
  "document_a_type_code",
  "document_b_type_code",
  "document_c_type_code"
]);
const CASE_FIELD_EXCLUDED_NAMES = new Set(["client_company_id", "client_software_version"]);
const HIDDEN_CASE_FIELD_NAMES = new Set([
  ...STEP_ONE_CASE_FIELD_NAMES,
  ...CASE_FIELD_EXCLUDED_NAMES
]);
const shouldDisplayCaseField = (field = {}, forcedVisibleFields) =>
  !HIDDEN_CASE_FIELD_NAMES.has(field.field_Name) &&
  (
    field.required ||
    ALWAYS_VISIBLE_OPTIONAL_FIELDS.has(field.field_Name) ||
    forcedVisibleFields?.has?.(field.field_Name) ||
    (Array.isArray(forcedVisibleFields) && forcedVisibleFields.includes(field.field_Name))
  );

const formatSocialSecurityNumber = (value = "") => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 5));
  if (digits.length > 5) parts.push(digits.slice(5));
  return parts.join("-");
};

const getDuplicateResponseMessage = (data = {}) => {
  const candidates = [
    data?.message,
    data?.result?.message,
    data?.result?.description,
    data?.result
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return "";
};

const parseDuplicateCheckResponse = (data = {}) => {
  const message = getDuplicateResponseMessage(data);
  const booleanCandidates = [
    data?.isDuplicate,
    data?.is_duplicate,
    data?.result,
    data?.result?.isDuplicate,
    data?.result?.is_duplicate,
    data?.result?.duplicateFound,
    data?.result?.duplicatesFound
  ];

  let resolvedBoolean;
  for (const candidate of booleanCandidates) {
    if (typeof candidate === "boolean") {
      resolvedBoolean = candidate;
      break;
    }
  }

  if (typeof resolvedBoolean !== "boolean" && message) {
    const normalizedMessage = message.toLowerCase();
    if (normalizedMessage.includes("no duplicate")) {
      resolvedBoolean = false;
    } else if (
      normalizedMessage.includes("duplicate case") ||
      normalizedMessage.includes("duplicate ssn")
    ) {
      resolvedBoolean = true;
    }
  }

  return {
    isDuplicate: resolvedBoolean === true,
    message
  };
};

const isDateFieldName = (fieldName = "") =>
  typeof fieldName === "string" && fieldName.toLowerCase().includes("date");

const Everify = () => {
  const navigate = useNavigate();
  const { setActiveCaseNumber } = useCase();
  const [formData, setFormData] = useState({
    citizenshipStatusCode: null,
    documentAType: null,
    documentBType: null,
    documentCType: null,
    socialSecurityNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingCase, setIsSubmittingCase] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [caseFields, setCaseFields] = useState([]);
  const [caseFieldValues, setCaseFieldValues] = useState({});
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState(null);
  const [lastCheckedSSN, setLastCheckedSSN] = useState(null);
  const selectedCitizenship = formData.citizenshipStatusCode?.value;
  const selectedDocumentAType = formData.documentAType?.value || "";
  const selectedDocumentBType = formData.documentBType?.value || "";

  const dropdownOptionMap = useMemo(
    () => ({
      citizenship_status_code: citizenshipOptions,
      document_a_type_code: documentAOptions,
      document_b_type_code: documentBOptions,
      document_c_type_code: documentCOptions,
      document_sub_type_code: documentSubTypeOptions,
      us_state_code: usStateOptions,
      reason_for_delay_code: reasonForDelayOptions,
      country_code: countryCodes
    }),
    []
  );

  const [isDelayExplanationRequired, setIsDelayExplanationRequired] = useState(false);
  useEffect(() => {
    const hireDate = caseFieldValues.date_of_hire;
    if (!hireDate || isNaN(new Date(hireDate).getTime())) {
      setIsDelayExplanationRequired(false);
      return;
    }
    countBusinessDaysSince(new Date(hireDate))
      .then((days) => setIsDelayExplanationRequired(days > 3))
      .catch(() => setIsDelayExplanationRequired(false));
  }, [caseFieldValues.date_of_hire]);

  const shouldForceAlienI94Fields = useMemo(
    () =>
      selectedCitizenship === "ALIEN_AUTHORIZED_TO_WORK" &&
      selectedDocumentAType === "FOREIGN_PASSPORT_WITH_FORM_I94",
    [selectedCitizenship, selectedDocumentAType]
  );
  const forcedCaseFieldNames = useMemo(() => {
    const names = new Set();
    if (shouldForceAlienI94Fields) {
      names.add("alien_number");
      names.add("alient_number");
      names.add("i94_number");
      names.add("alienNumber");
      names.add("i94Number");
    }
    if (selectedDocumentAType) {
      names.add("document_a_expiration_date");
      names.add("documentAExpirationDate");
    }
    if (selectedDocumentBType) {
      names.add("document_b_expiration_date");
      names.add("documentBExpirationDate");
    }
    return names;
  }, [shouldForceAlienI94Fields, selectedDocumentAType, selectedDocumentBType]);
  const filteredCaseFields = useMemo(
    () => caseFields.filter(field => shouldDisplayCaseField(field, forcedCaseFieldNames)),
    [caseFields, forcedCaseFieldNames]
  );

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(1);
      setSaveError("");
      return;
    }
    navigate("/");
  };

  const clearFieldError = (fieldName) => {
    setErrors(prev => {
      if (!prev[fieldName]) return prev;
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

  const updateFormField = (fieldName, nextValue) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: typeof nextValue === "function" ? nextValue(prev) : nextValue
    }));
    clearFieldError(fieldName);
  };

  const initializeCaseFieldValues = (fields) => {
    const defaults = {
      citizenship_status_code: formData.citizenshipStatusCode?.value || "",
      ssn: formData.socialSecurityNumber || "",
      document_a_type_code: formData.documentAType?.value || "",
      document_b_type_code: formData.documentBType?.value || "",
      document_c_type_code: formData.documentCType?.value || ""
    };
    return fields.reduce((acc, field) => {
      if (CASE_FIELD_EXCLUDED_NAMES.has(field.field_Name)) {
        return acc;
      }
      const baseValue = defaults[field.field_Name] || "";
      acc[field.field_Name] = isDateFieldName(field.field_Name)
        ? clampIsoDateYear(baseValue)
        : baseValue;
      return acc;
    }, {});
  };

  const handleCaseFieldChange = (fieldName, value) => {
    if (CASE_FIELD_EXCLUDED_NAMES.has(fieldName)) return;
    let processedValue = value;
    if (fieldName === "ssn") {
      processedValue = formatSocialSecurityNumber(value);
    } else if (fieldName === "case_creator_phone_number") {
      processedValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (fieldName === "case_creator_phone_number_extension") {
      processedValue = value.replace(/\s+/g, "").slice(0, 6);
    } else if (fieldName === "us_passport_number") {
      processedValue = value.replace(/\s+/g, "").toUpperCase().slice(0, 9);
    } else if (fieldName === "employee_state") {
      processedValue = value.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 3);
    } else if (fieldName === "employee_zip_code") {
      const digitsOnly = value.replace(/\D/g, "");
      processedValue = digitsOnly.length >= 5 ? digitsOnly.slice(0, 5) : digitsOnly.slice(0, 7);
    } else if (fieldName === "middle_initial") {
      processedValue = value.slice(0, 1).toUpperCase();
    } else if (fieldName === "employer_case_id") {
      processedValue = value.trim();
    } else if (isDateFieldName(fieldName)) {
      processedValue = clampIsoDateYear(value);
    }
    setCaseFieldValues(prev => ({
      ...prev,
      [fieldName]: processedValue
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "socialSecurityNumber") {
      setDuplicateResult(null);
      updateFormField(name, formatSocialSecurityNumber(value));
      return;
    }
    if (isDateFieldName(name)) {
      updateFormField(name, clampIsoDateYear(value));
      return;
    }
    updateFormField(name, value);
  };

  const handleSelectChange = (field, option) => {
    updateFormField(field, option);
  };

  const handleDocumentAChange = (option) => {
    updateFormField("documentAType", option);
    if (option) {
      updateFormField("documentBType", null);
      updateFormField("documentCType", null);
      clearFieldError("documentBType");
      clearFieldError("documentCType");
    }
  };

  const handleDocumentBChange = (option) => {
    setFormData(prev => {
      const next = {
        ...prev,
        documentBType: option,
        documentCType: null,
      };
      if (option && prev.documentAType) {
        next.documentAType = null;
      }
      return next;
    });
    clearFieldError("documentBType");
    clearFieldError("documentCType");
    if (formData.documentAType) {
      clearFieldError("documentAType");
    }
  };

  const ssnDigits = useMemo(
    () => formData.socialSecurityNumber.replace(/\D/g, ""),
    [formData.socialSecurityNumber]
  );
  const isValidSSN = ssnDigits.length === 9;
  const shouldShowInlineInvalidSSN = ssnDigits.length > 0 && !isValidSSN;
  const isDuplicateSSN = duplicateResult?.isDuplicate === true;
  const hasCheckedDuplicate = duplicateResult !== null;

  const canProceedAfterDuplicateCheck =
    hasCheckedDuplicate && !isDuplicateSSN;

  const handleCheckDuplicate = useCallback(
    async (force = false) => {
      if (isCheckingDuplicate) return;
      if (!ssnDigits || ssnDigits.length !== 9) {
        toast.info("Enter a valid SSN before checking for duplicates.");
        return;
      }
      if (!force && lastCheckedSSN === ssnDigits) {
        return;
      }

      setIsCheckingDuplicate(true);
      setDuplicateResult(null);
      setLastCheckedSSN(ssnDigits);

      try {
        const formattedRequest = formatSocialSecurityNumber(ssnDigits);
        const encodedRequest = encodeURIComponent(formattedRequest);
        const { data } = await api.post(
          `/everify/duplicatecheck?request=${encodedRequest}`
        );

        const duplicateCheckResult = parseDuplicateCheckResponse(data);
        setDuplicateResult(duplicateCheckResult);

        if (duplicateCheckResult.isDuplicate) {
          toast.warning(duplicateCheckResult.message || "Duplicate SSN detected");
        } else {
          toast.success(duplicateCheckResult.message || "No duplicate case found");
        }
      } catch (error) {
        toast.error("Failed to check duplicate SSN");
      } finally {
        setIsCheckingDuplicate(false);
      }
    },
    [isCheckingDuplicate, lastCheckedSSN, ssnDigits]
  );

  useEffect(() => {
    if (!isValidSSN) {
      if (lastCheckedSSN !== null && ssnDigits.length < 9) {
        setLastCheckedSSN(null);
      }
      return;
    }
    if (isCheckingDuplicate || lastCheckedSSN === ssnDigits) {
      return;
    }
    handleCheckDuplicate();
  }, [
    handleCheckDuplicate,
    isCheckingDuplicate,
    isValidSSN,
    lastCheckedSSN,
    ssnDigits
  ]);

  const shouldShowDocumentC = !formData.documentAType?.value;
  const handleCaseFieldsSubmit = async () => {
    const payload = buildCaseSubmissionPayload();
    setIsSubmittingCase(true);
    try {
      const { data } = await api.post(CREATE_CASE_ENDPOINT, payload);
      const caseNumber =
        data?.caseNumber ||
        data?.case_number ||
        data?.caseNum ||
        data?.case_num ||
        data?.result?.caseNumber ||
        data?.result?.case_number ||
        "";
      if (caseNumber) {
        setActiveCaseNumber(String(caseNumber));
      }
      toast.success("Case created successfully.");
      navigate("/");
    } catch (error) {
      toast.error("Unable to submit case. Please try again.");
    } finally {
      setIsSubmittingCase(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.citizenshipStatusCode?.value) {
      newErrors.citizenshipStatusCode = "Citizenship or Immigration Status is required.";
    }

    const ssnDigits = formData.socialSecurityNumber.replace(/\D/g, "");
    if (ssnDigits.length !== 9) {
      newErrors.socialSecurityNumber = "Enter a 9-digit SSN.";
    }

    const hasDocumentA = !!formData.documentAType?.value;
    const hasDocumentB = !!formData.documentBType?.value;
    if (!hasDocumentA && !hasDocumentB) {
      newErrors.documentAType = "Select a Document A type or choose Document B + C instead.";
      newErrors.documentBType = "Select a Document B type or choose Document A instead.";
    }
    if (!hasDocumentA && hasDocumentB && !formData.documentCType?.value) {
      newErrors.documentCType = "Document C type is required when Document B is chosen.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    const payload = {
      citizenship_status_code: formData.citizenshipStatusCode.value,
      ssn: formData.socialSecurityNumber
    };
    if (formData.documentAType?.value) {
      payload.document_a_type_code = formData.documentAType.value;
    } else if (formData.documentBType?.value && formData.documentCType?.value) {
      payload.document_b_type_code = formData.documentBType.value;
      payload.document_c_type_code = formData.documentCType.value;
    }
    return payload;
  };

  // const buildCaseSubmissionPayload = () => {
  //   const payload = { ...caseFieldValues };
  //   const requiredFields = buildPayload();
  //   Object.entries(requiredFields).forEach(([key, value]) => {
  //     payload[key] = value;
  //   });
  //   CASE_FIELD_EXCLUDED_NAMES.forEach(fieldName => {
  //     if (fieldName in payload) {
  //       delete payload[fieldName];
  //     }
  //   });
  //   Object.entries(payload).forEach(([key, value]) => {
  //     if (value === "" || value === undefined) {
  //       delete payload[key];
  //     }
  //   });
  //   payload.my_i9 = payload.my_i9 ?? null;
  //   return payload;
  // };

  const buildCaseSubmissionPayload = () => {
    const payload = { ...caseFieldValues };

    const requiredFields = buildPayload();
    Object.entries(requiredFields).forEach(([key, value]) => {
      payload[key] = value;
    });

    // 🔥 FIX STARTS HERE
    const isDocumentBCFlow =
      !!formData.documentBType?.value &&
      !!formData.documentCType?.value &&
      !formData.documentAType?.value;

    if (isDocumentBCFlow) {
      payload.no_expiration_date = true;

      // safety: remove expiration dates if present
      delete payload.document_b_expiration_date;
      delete payload.document_c_expiration_date;
      delete payload.expiration_date;
    }
    // 🔥 FIX ENDS HERE

    CASE_FIELD_EXCLUDED_NAMES.forEach(fieldName => {
      if (fieldName in payload) {
        delete payload[fieldName];
      }
    });

    Object.entries(payload).forEach(([key, value]) => {
      if (value === "" || value === undefined) {
        delete payload[key];
      }
    });

    payload.my_i9 = payload.my_i9 ?? null;
    return payload;
  };


  const handleSave = async () => {
    if (!validateForm()) {
      setSaveError("");
      return;
    }
    setSaveError("");
    const payload = buildPayload();
    setIsSaving(true);
    try {
      const { data } = await api.post(CASE_FIELDS_ENDPOINT, payload);
      const fields = data?.case_Fields || data?.case_fields || [];
      setCaseFields(fields);
      setCaseFieldValues(initializeCaseFieldValues(fields));
      setCurrentStep(2);
    } catch (error) {
      setSaveError("Please check your details");
    } finally {
      setIsSaving(false);
    }
  };

  const renderDocumentStep = () => {
    const aActive = !!formData.documentAType?.value;
    const bcActive = !!(formData.documentBType?.value || formData.documentCType?.value);
    return (
      <div className="step-content citizenship-documents-layout">
        <div className="ev-section-header">
          <h2 className="ev-section-title">I-9 Information</h2>
          <p className="ev-section-subtitle">
            Enter the employee&apos;s details exactly as recorded on their I-9 form.
          </p>
        </div>

        <div className="ev-primary-fields">
          <div className="ev-field-group">
            <div className="ssn-field-group">
              <label htmlFor="socialSecurityNumber" className="ev-field-label">
                Social Security Number{" "}
                <span className="required-asterisk">*</span>
              </label>
              <div className="ssn-input-row">
                <Input
                  label={null}
                  name="socialSecurityNumber"
                  value={formData.socialSecurityNumber}
                  onChange={handleInputChange}
                  className="ssn-field"
                  placeholder="XXX-XX-XXXX"
                  isError={errors.socialSecurityNumber}
                  maxLength={11}
                />
              </div>
              {shouldShowInlineInvalidSSN && (
                <div className="error-message">Enter a valid 9-digit SSN before proceeding.</div>
              )}
              {errors.socialSecurityNumber && (
                <div className="error-message">{errors.socialSecurityNumber}</div>
              )}
              {isValidSSN && isCheckingDuplicate && (
                <div className="ssn-duplicate-status ssn-duplicate-checking">
                  <span className="ssn-duplicate-spinner" />
                  Checking for duplicates&hellip;
                </div>
              )}
              {isValidSSN && !isCheckingDuplicate && duplicateResult !== null && (
                <div className={`ssn-duplicate-status ${duplicateResult.isDuplicate ? "ssn-duplicate-found" : "ssn-duplicate-clear"}`}>
                  {duplicateResult.isDuplicate
                    ? <>&#10007; {duplicateResult.message || "Duplicate SSN detected. Resolve before continuing."}</>
                    : <>&#10003; No duplicate case found.</>}
                </div>
              )}
            </div>
          </div>

          <div className="ev-field-group">
            <CustomSingleSelect
              label={<>Citizenship or Immigration Status{" "}<span className="required-asterisk">*</span></>}
              name="citizenshipStatusCode"
              selectedValue={formData.citizenshipStatusCode}
              onChange={(option) => handleSelectChange("citizenshipStatusCode", option)}
              options={citizenshipOptions}
              placeholder="Select status"
              isError={errors.citizenshipStatusCode}
              className="citizenship-select"
            />
            {errors.citizenshipStatusCode && (
              <div className="error-message">{errors.citizenshipStatusCode}</div>
            )}
          </div>
        </div>

        <div className="ev-doc-section">
          <div className="ev-doc-section-header">
            <span className="ev-doc-eyebrow">Document Verification</span>
            <p className="ev-doc-hint">
              Select <strong>one document from List A</strong>, or provide <strong>one from List B and one from List C</strong>.
            </p>
          </div>

          <div className={`ev-doc-columns${aActive ? " ev-doc-columns--a-active" : ""}${bcActive ? " ev-doc-columns--bc-active" : ""}`}>
            <div className="ev-doc-col ev-doc-col--a">
              <div className="ev-doc-col-header">
                <span className="ev-doc-col-tag">List A</span>
                <p className="ev-doc-col-desc">
                  A single document establishing both identity and employment authorization
                </p>
              </div>
              <CustomSingleSelect
                label={<>Document type{!formData.documentBType?.value && <span className="required-asterisk">*</span>}</>}
                name="documentAType"
                selectedValue={formData.documentAType}
                onChange={handleDocumentAChange}
                options={documentAOptions}
                placeholder="Select List A document"
                isError={errors.documentAType}
                className="document-select"
              />
              {errors.documentAType && (
                <div className="error-message">{errors.documentAType}</div>
              )}
            </div>

            <div className="ev-doc-or">
              <div className="ev-doc-or__line" />
              <span className="ev-doc-or__label">or</span>
              <div className="ev-doc-or__line" />
            </div>

            <div className="ev-doc-col ev-doc-col--bc">
              <div className="ev-doc-col-header">
                <span className="ev-doc-col-tag ev-doc-col-tag--bc">List B + C</span>
                <p className="ev-doc-col-desc">
                  Two documents — one for identity, one for employment authorization
                </p>
              </div>
              <div className="ev-doc-bc-stack">
                <div>
                  <CustomSingleSelect
                    label={<>List B — Identity{!formData.documentAType?.value && <span className="required-asterisk">*</span>}</>}
                    name="documentBType"
                    selectedValue={formData.documentBType}
                    onChange={handleDocumentBChange}
                    options={documentBOptions}
                    placeholder="Select List B document"
                    isError={errors.documentBType}
                    className="document-select"
                  />
                  {errors.documentBType && (
                    <div className="error-message">{errors.documentBType}</div>
                  )}
                </div>
                {shouldShowDocumentC && (
                  <div>
                    <CustomSingleSelect
                      label={<>List C — Work Authorization{!formData.documentAType?.value && <span className="required-asterisk">*</span>}</>}
                      name="documentCType"
                      selectedValue={formData.documentCType}
                      onChange={(option) => handleSelectChange("documentCType", option)}
                      options={documentCOptions}
                      placeholder="Select List C document"
                      isError={errors.documentCType}
                      className="document-select"
                    />
                    {errors.documentCType && (
                      <div className="error-message">{errors.documentCType}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="everify-wrapper">
      <Header showAdminCompanies={false} handleCancel={handleBack} />
      <div className="everify-container p-4">
        <div className="everify-steps mb-4">
          <div className="stepper">
            <div className="step-item">
              <div className={`step ${currentStep >= 1 ? "active" : ""}`}>1</div>
              <span className="step-label">I-9 Information</span>
            </div>
            <div className={`step-connector ${currentStep >= 2 ? "active" : ""}`} aria-hidden="true" />
            <div className="step-item">
              <div className={`step ${currentStep >= 2 ? "active" : ""}`}>2</div>
              <span className="step-label">E-Verify Case Details</span>
            </div>
          </div>
        </div>

        <div className="everify-form-container p-4 m-4">
          {currentStep === 1 ? renderDocumentStep() : (
            <CaseFieldsStep
              caseFields={filteredCaseFields}
              caseFieldValues={caseFieldValues}
              onFieldChange={handleCaseFieldChange}
              dropdownOptionMap={dropdownOptionMap}
              isDelayExplanationRequired={isDelayExplanationRequired}
              forcedRequiredFields={forcedCaseFieldNames}
            />
          )}

          {currentStep === 1 && saveError && <div className="save-message mt-3 error-message">{saveError}</div>}

          <div className="everify-actions d-flex justify-content-between align-items-center mt-4">
            <Button
              label="Back"
              onClick={handleBack}
              type="button"
              secondary={true}
            />
            {currentStep === 1 ? (
              <Button
                label="Next"
                onClick={handleSave}
                type="button"
                primary={canProceedAfterDuplicateCheck}
                secondary={!canProceedAfterDuplicateCheck}
                isLoading={isSaving}
                isDisabled={isSaving || !canProceedAfterDuplicateCheck}
              />
            ) : (
              <Button
                label="Submit Case"
                onClick={handleCaseFieldsSubmit}
                type="button"
                primary={true}
                isLoading={isSubmittingCase}
                isDisabled={isSubmittingCase}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Everify;
